<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Edit'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-contractors' }">{{
            $t("CONTRACTORS")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("EDIT") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="example-tools justify-content-center"></div>
      </template>
      <template v-slot:body>
        <b-row>
          <b-col sm>
            <v-text-field
              :value="form.name"
              :label="$t('CONTRACTOR')"
              outlined
              disabled
              readonly
            ></v-text-field>
          </b-col>
        </b-row>
        <b-form>
          <FormBuilder
            v-if="!firstLoader"
            :form="form"
            :schemaJson="schemaJson"
            :v="$v"
          ></FormBuilder>

          <SkeletonLoaderMain :visible="firstLoader"></SkeletonLoaderMain>
          <v-overlay :value="!firstLoader && isLoadingContractor">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>
        </b-form>
        <v-overlay :value="!firstLoader && isLoadingContractor">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </template>
      <template v-slot:foot>
        <div
          v-show="_.size(errors) > 0"
          role="alert"
          class="alert alert-danger"
        >
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error }}
          </div>
        </div>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <DeleteButton
            v-on:onDelete="onDelete()"
            v-permission="['suppliers-delete']"
          ></DeleteButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingContractor"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  FETCH_CONTRACTOR,
  UPDATE_CONTRACTOR,
  DELETE_CONTRACTOR
} from "@/modules/contractor/store/contractor.module";

import { FETCH_SELECTS_BASIC } from "@/core/services/store/shared_base.module";

import FormBuilder from "@/view/content/forms/FormBuilder.vue";
import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import { validationMixin } from "vuelidate";

import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";

import KTCard from "@/view/content/Card.vue";
import { mapGetters, mapState } from "vuex";

export default {
  mixins: [validationMixin, formBuilderMixin],
  data() {
    return {
      errors: {},
      firstLoader: true,
      currencies: {},
      vatGroups: {},
      contractor: {},
      isEdit: true
    };
  },
  components: {
    KTCard,
    FormBuilder,
    SaveButtonDropdown
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Contractors", route: { name: "list-contractors" } },
      { title: "Edit Contractor" }
    ]);
    vm.getSelects();
    vm.getContractor();
  },
  watch: {},
  computed: {
    ...mapGetters(["isLoadingContractor", "currentUser"]),
    ...mapState({
      errors: state => state.auth.errors
    }),
    schemaJson() {
      return {
        fields: [
          {
            type: "input",
            inputType: "number",
            id: "contractor-number-input",
            groupId: "contractor-number-group",
            required: "required",
            feedback_id: "contractor-live-feedback",
            divider: false,
            i18n: {
              label: "NUMBER",
              placeholder: "NUMBER",
              validation: "VALID_NUMBER"
            },
            validations: {
              required: true,
              minLength: 1
            },
            translatable: false,
            model: "number"
          },
          {
            type: "input",
            inputType: "text",
            id: "contractor-name-input",
            groupId: "contractor-name-group",
            required: "required",
            feedback_id: "contractor-live-feedback",
            divider: false,
            i18n: {
              label: "NAME",
              placeholder: "NAME",
              validation: "VALID_NAME"
            },
            validations: {
              required: true,
              minLength: 2
            },
            translatable: false,
            model: "name"
          },
          {
            type: "input",
            inputType: "text",
            id: "contractor-bulstat-input",
            groupId: "contractor-bulstat-group",
            required: "required",
            feedback_id: "contractor-live-feedback",
            divider: false,
            i18n: {
              label: "BULSTAT",
              placeholder: "BULSTAT",
              validation: "VALID_BULSTAT"
            },
            validations: {},
            translatable: false,
            model: "bulstat"
          },
          {
            type: "input",
            inputType: "text",
            id: "contractor-vat_number-input",
            groupId: "contractor-vat_number-group",
            required: "required",
            feedback_id: "contractor-live-feedback",
            divider: false,
            i18n: {
              label: "VAT_NUMBER",
              placeholder: "VAT_NUMBER",
              validation: "VALID_VAT_NUMBER"
            },
            validations: {},
            translatable: false,
            model: "vatNumber"
          },
          {
            type: "input",
            inputType: "text",
            id: "contractor-owner-input",
            groupId: "contractor-owner-group",
            required: "required",
            feedback_id: "contractor-live-feedback",
            divider: true,
            i18n: {
              label: "OWNER",
              placeholder: "OWNER",
              validation: "VALID_OWNER"
            },
            validations: {},
            translatable: false,
            model: "owner"
          },
          {
            type: "input",
            inputType: "text",
            id: "contractor-address-input",
            groupId: "contractor-address-group",
            required: "required",
            feedback_id: "contractor-live-feedback",
            divider: false,
            i18n: {
              label: "ADDRESS",
              placeholder: "ADDRESS",
              validation: "VALID_ADDRESS"
            },
            validations: {},
            translatable: false,
            model: "address"
          },
          {
            type: "select",
            inputType: "select",
            groupId: "contractor-currency-group",
            id: "contractor-currency-input",
            feedback_id: "contractor-currency-live-feedback",
            divider: false,
            clearable: false,
            i18n: {
              label: "CURRENCY",
              placeholder: "CURRENCY"
            },
            options: this.currencies,
            validations: {
              required: true
            },
            model: "currencyId"
          },
          {
            type: "select",
            inputType: "select",
            id: "contractor-vat_rate-input",
            groupId: "contractor-vat_rate-group",
            required: "required",
            feedback_id: "contractor-vat_rate-live-feedback",
            divider: false,
            i18n: {
              label: "VAT_RATE",
              placeholder: "VAT_RATE"
            },
            options: this.vatGroups,
            validations: {},
            translatable: false,
            model: "vatGroupId"
          },
          {
            type: "switch",
            groupId: "contractor-isForeign-group",
            id: "contractor-isForeign-input",
            inputType: "checkbox",
            required: "required",
            feedback_id: "isForeign-live-feedback",
            colSm: "auto",
            divider: true,
            i18n: {
              label: "IS_FOREIGN",
              placeholder: "IS_FOREIGN"
            },
            validations: {},
            model: "isForeign"
          }
        ]
      };
    }
  },
  validations() {
    let vm = this;
    let tmpValidationObject = this.generateFormValidationsOutOfSchemaJson(
      this.schemaJson
    );
    const passwordValidations = {
      required: true,
      minLength: 6
    };
    if ("password" in vm.form) {
      tmpValidationObject.form["password"] = vm.setItemValidations(
        passwordValidations
      );
      return tmpValidationObject;
    } else {
      return tmpValidationObject;
    }
  },
  methods: {
    getSelects() {
      let vm = this;
      let payload = {
        currencies: {
          fields: ["id", "name", "code"],
          search: ""
        },
        vat_groups: {
          fields: ["id", "name", "percent"],
          search: ""
        }
      };
      this.$store
        .dispatch(FETCH_SELECTS_BASIC, payload)
        .then(data => {
          vm.currencies = data.data.currencies;
          vm.vatGroups = data.data.vat_groups;
        })
        .catch(response => {
          console.log(response);
        });
    },
    getContractor() {
      let vm = this;
      let id = this.$route.params.id;
      this.$store
        .dispatch(FETCH_CONTRACTOR, id)
        .then(data => {
          // vm.$notify({
          //   group: "notify",
          //   type: "success",
          //   title: "<i class='flaticon2-checkmark'></i> Success",
          //   text: data.message
          // });
          vm.$nextTick(function() {
            vm.contractor = data.data;
            this.generateFormOutOfSchemaJson(
              this.schemaJson,
              vm.contractor
            ).then(() => {
              vm.prepareChangesCheck();
              vm.firstLoader = false;
            });
          });
        })
        .catch(response => {
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
            this.$router.push({ name: "list-contractors" });
          }
        });
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    onSubmit(evt) {
      evt.preventDefault();
    },
    onSave(next) {
      let vm = this;
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> Warning",
          text: "Please fill all required fields correctly"
        });
        return;
      }
      this.$store
        .dispatch(UPDATE_CONTRACTOR, {
          id: vm.contractor.id,
          payload: this.form
        })
        .then(data => {
          vm.errors = data.data.errors;
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });

          vm.prepareChangesCheck().then(() => {
            // let itemEdit = data.data;
            if (next == "continue") {
              //New
              // this.$router.push({
              //   name: "edit-contractor",
              //   params: { id: itemEdit.id }
              // });
            }
            if (next == "new") {
              //New
              this.$router.push({ name: "add-contractor" });
            }
            if (next == "exit") {
              this.$router.push({ name: "list-contractors" });
            }
          });
        })
        .catch(function(response) {
          vm.errors = response.data.errors;
          vm.$notify({
            group: "notify",
            type: "error",
            title: "Error",
            text: response.data.message
          });
        });
    },
    onDelete() {
      let vm = this;
      let message = vm.$t("PLEASE_CONFIRM");
      this.$dialog
        .confirm(message)
        .then(function() {
          vm.$store
            .dispatch(DELETE_CONTRACTOR, vm.contractor.id)
            .then(data => {
              vm.$notify({
                group: "notify",
                type: "success",
                title: "<i class='flaticon2-checkmark'></i> Success",
                text: data.message
              });

              vm.prepareChangesCheck().then(() => {
                vm.$router.push({ name: "list-contractors" });
              });
            })
            .catch(function(response) {
              vm.$notify({
                group: "notify",
                type: "error",
                title: "Error",
                text: response.data.message
              });
            });
        })
        .catch(function() {});
    },
    resetVForm() {
      this.$v.$reset();
    }
  }
};
</script>
